import React, {
    createContext, useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import { useAuth } from './AuthContext';
import { getMyFlags, getPublicFlags } from '../services/api';
import { hydrationFlagsSeed, prerenderState } from '../seo/prerenderState';

// Runtime feature flags (#842). Resolved SERVER-side per user (so allowlist
// membership never ships to the client) and fail-closed: signed-out users,
// fetch errors, and unknown flags all read as off.
//
// ON DEMAND (#3615, founder 2026-10-09): after the graduated flags were
// retired, two remain (astra_lite, the signed-out Astra kill switch, and
// patent_billing), read by a handful of surfaces. The provider fetches
// nothing until a mounted consumer asks: a consumer subscribes to the
// signed-in read ('mine', get-my-flags) and/or the public read ('public',
// get-public-flags), and the refetch on focus and on the slow interval runs
// only while some subscriber is mounted. (Before this, every page view
// fetched both, on mount, every five minutes and on every focus: about 600
// of profile-manager's daily requests.) Since Astra Today (#3754) the
// signed-in read runs on every signed-in page again, because Astra's sidebar
// row and phone pill read astra_today for the "1 thing for you" words; the
// public read stays on demand. Cost: profile-manager already keeps one warm
// instance, so a few hundred small reads a day add nothing measurable.
//
// Flag flips land WITHOUT a deploy for the pages that read them: they refetch
// when the tab regains focus and on a slow interval. Deliberately no
// Firestore snapshot listener — that would need a public rules carve-out;
// server-mediated reads keep the rules surface untouched.
const REFRESH_MS = 5 * 60 * 1000;

const NO_SUBSCRIBE = () => () => {};

const FeatureFlagsContext = createContext({
    flags: {}, ready: false, forEmail: null, publicFlags: {}, publicReady: false,
    refresh: () => {}, subscribe: NO_SUBSCRIBE,
});

export function FeatureFlagsProvider({ children }) {
    const { currentUser } = useAuth();
    // `forEmail` records WHOSE flags these are. Consumers that act on a
    // user's flags must check it: on the render where a user first appears,
    // `ready` alone can be a stale true from the signed-out resolution.
    const [state, setState] = useState({ flags: {}, ready: false, forEmail: null });
    // Public GA flags (mode 'on') — resolved WITHOUT auth, so they load for
    // signed-out visitors too. Prerendering (#2567) starts from the public
    // flags fetched at build time; hydrating a prerendered page (#2605)
    // starts from the flags it was built with, so the first render matches
    // the markup; a subscriber's fetch then refreshes them.
    const [pub, setPub] = useState(() => {
        const pre = prerenderState();
        if (pre) return { publicFlags: pre.publicFlags || {}, publicReady: true };
        const seed = hydrationFlagsSeed();
        return seed ? { publicFlags: seed, publicReady: true } : { publicFlags: {}, publicReady: false };
    });
    // How many mounted consumers want each read. Counts, not booleans: two
    // consumers on one page share one fetch, and the read stops only when
    // the last one unmounts.
    const [demand, setDemand] = useState({ mine: 0, public: 0 });
    const subscribe = useCallback(({ mine = false, public: wantsPublic = false } = {}) => {
        if (!mine && !wantsPublic) return () => {};
        const delta = (sign) => (d) => ({
            mine: d.mine + (mine ? sign : 0),
            public: d.public + (wantsPublic ? sign : 0),
        });
        setDemand(delta(1));
        return () => setDemand(delta(-1));
    }, []);
    const wantMine = demand.mine > 0;
    const wantPublic = demand.public > 0;

    const emailRef = useRef(null);
    emailRef.current = currentUser?.email || null;

    const refresh = useCallback(async (attempt = 0) => {
        const email = emailRef.current;
        if (!email) {
            setState({ flags: {}, ready: true, forEmail: null });
            return;
        }
        try {
            const flags = await getMyFlags(email);
            setState({ flags: flags || {}, ready: true, forEmail: email });
        } catch {
            // Cold-load token race (#319): the first backend call can fire
            // before the Firebase token is ready and 401. Retry a few times
            // BEFORE failing closed; stay not-ready across retries.
            if (attempt < 4) {
                setTimeout(() => refresh(attempt + 1), 400 * (attempt + 1));
                return;
            }
            // fail closed after retries - but STAMP the email: consumers
            // gated on forEmail must proceed (with empty flags) rather than
            // wait forever on a flaky network.
            setState((s) => ({ flags: s.flags, ready: true, forEmail: email }));
        }
    }, []);

    const refreshPublic = useCallback(async () => {
        try {
            const pf = await getPublicFlags();
            setPub({ publicFlags: pf || {}, publicReady: true });
        } catch {
            // Fail closed to "nothing GA".
            setPub((p) => ({ publicFlags: p.publicFlags, publicReady: true }));
        }
    }, []);

    useEffect(() => {
        if (!wantMine) return undefined;
        // A signed-in user's flags must RESOLVE before `ready`: reset here;
        // refresh sets it true only once THIS user's fetch answers.
        if (currentUser?.email) setState((s) => ({ ...s, ready: false }));
        refresh();
        if (!currentUser?.email) return undefined;
        const onVisible = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        document.addEventListener('visibilitychange', onVisible);
        const id = setInterval(refresh, REFRESH_MS);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            clearInterval(id);
        };
    }, [wantMine, currentUser?.email, refresh]);

    useEffect(() => {
        if (!wantPublic) return undefined;
        refreshPublic();
        const onVisible = () => {
            if (document.visibilityState === 'visible') refreshPublic();
        };
        document.addEventListener('visibilitychange', onVisible);
        const id = setInterval(refreshPublic, REFRESH_MS);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            clearInterval(id);
        };
    }, [wantPublic, refreshPublic]);

    return (
        <FeatureFlagsContext.Provider value={{ ...state, ...pub, refresh, subscribe }}>
            {children}
        </FeatureFlagsContext.Provider>
    );
}

/**
 * The flags, subscribing to the reads this consumer needs. `mine`: the
 * signed-in user's resolved flags (get-my-flags; nothing is fetched while
 * signed out). `public`: the flags on for everyone (get-public-flags).
 */
export function useFeatureFlags({ mine = true, public: wantsPublic = true } = {}) {
    const ctx = useContext(FeatureFlagsContext);
    const subscribe = ctx.subscribe || NO_SUBSCRIBE;
    useEffect(() => subscribe({ mine, public: wantsPublic }), [subscribe, mine, wantsPublic]);
    return ctx;
}

/** True only when the signed-in user's resolved flags include `name: true`. */
export function useFeatureFlag(name) {
    return !!useFeatureFlags({ mine: true, public: false }).flags[name];
}
